import type { Locale } from '@iac/contracts';
import { useState } from 'react';
import type { Dictionary } from '../i18n/dictionary';
import type { Story } from '../lib/story';
import { Combobox } from './Combobox';

interface Product {
  id: string;
  name: string;
  kcal: number;
}

const sample: Dictionary<{
  label: string;
  newLabel: (text: string) => string;
  products: Product[];
}> = {
  pl: {
    label: 'Produkt',
    newLabel: (text) => `Dodaj nowy: ${text}`,
    products: [
      { id: '1', name: 'Płatki owsiane', kcal: 370 },
      { id: '2', name: 'Mleko 2%', kcal: 50 },
      { id: '3', name: 'Banan', kcal: 89 },
      { id: '4', name: 'Żółty ser', kcal: 356 },
      { id: '5', name: 'Chleb żytni', kcal: 250 },
    ],
  },
  en: {
    label: 'Product',
    newLabel: (text) => `Add new: ${text}`,
    products: [
      { id: '1', name: 'Rolled oats', kcal: 370 },
      { id: '2', name: 'Milk 2%', kcal: 50 },
      { id: '3', name: 'Banana', kcal: 89 },
      { id: '4', name: 'Gouda cheese', kcal: 356 },
      { id: '5', name: 'Rye bread', kcal: 250 },
    ],
  },
};

/** The combobox owns no state; the form around it does. */
function ProductPicker({ locale }: { locale: Locale }) {
  const t = sample[locale];
  const [name, setName] = useState('');
  return (
    <Combobox
      id="story-combobox"
      label={t.label}
      value={name}
      onChange={setName}
      options={t.products}
      optionKey={(p) => p.id}
      optionLabel={(p) => p.name}
      renderOption={(p) => (
        <>
          <span>{p.name}</span>
          <span className="text-hint">{p.kcal} kcal / 100 g</span>
        </>
      )}
      onPick={(p) => setName(p.name)}
      newLabel={t.newLabel}
    />
  );
}

export default {
  kind: 'component',
  title: { pl: 'Pole z podpowiedziami', en: 'Combobox' },
  description: {
    pl: 'Pole tekstowe z własną listą podpowiedzi — zamiennik `<datalist>`, które przeglądarki mobilne renderują źle albo wcale. Tekst jest wartością: wpisanie czegoś spoza listy jest dozwolone i lista kończy się wtedy wierszem z `newLabel`. Dopasowanie ignoruje wielkość liter i ogonki. Lista otwiera się po wpisaniu pierwszego znaku, nie po kliknięciu w pole; w pustym polu pokazują ją tylko strzałki.',
    en: 'A text field with its own suggestion list — the replacement for `<datalist>`, which mobile browsers render badly or not at all. The text is the value: typing something not in the list is allowed, and the list then ends with the `newLabel` row. Matching ignores case and accents. The list opens on the first typed character, not on a click into the field; in an empty field only the arrow keys show it.',
  },
  variants: [
    {
      name: { pl: 'Wybór produktu', en: 'Picking a product' },
      render: (locale) => <ProductPicker locale={locale} />,
    },
  ],
} satisfies Story;
